import { AnimatePresence, motion } from "framer-motion";

import { Icon, Span } from "@virtual-brain-lib/frontend-design-system/ui";
import { cn } from "@virtual-brain-lib/frontend-design-system/utils";
import type { MessageToolStep, MessageToolStepStatus } from "@virtual-brain-lib/types";

interface ToolCardStepsProps {
	readonly steps: readonly MessageToolStep[];
	readonly className?: string;
}

export function ToolCardSteps({ steps, className }: ToolCardStepsProps) {
	if (steps.length === 0) {
		return null;
	}

	return (
		<div className={cn("flex flex-col gap-1", className)}>
			<AnimatePresence initial={false}>
				{steps.map((step) => (
					<motion.div
						key={step.id}
						animate={{ opacity: 1, y: 0 }}
						className="flex items-center gap-1.5"
						exit={{ opacity: 0 }}
						initial={{ opacity: 0, y: 2 }}
						transition={{ duration: 0.2, ease: "easeOut" }}
					>
						<StepIcon status={step.status} />
						<StepLabel status={step.status}>{step.label}</StepLabel>
					</motion.div>
				))}
			</AnimatePresence>
		</div>
	);
}

function StepIcon({ status }: { readonly status: MessageToolStepStatus }) {
	return (
		<span className="flex size-3 shrink-0 items-center justify-center">
			<StepIconGlyph status={status} />
		</span>
	);
}

function StepIconGlyph({ status }: { readonly status: MessageToolStepStatus }) {
	if (status === "queued") {
		return <span className="size-1 rounded-full bg-secondary-border" />;
	}

	return (
		<Icon
			color="defaultLight"
			name={status === "processing" ? "spinner" : "circleCheck"}
			size="sm"
		/>
	);
}

const STEP_LABEL_EXTRA_CLASS: Record<MessageToolStepStatus, string> = {
	processing: "italic",
	processed: "",
	queued: "",
};

function StepLabel({
	status,
	children,
}: {
	readonly status: MessageToolStepStatus;
	readonly children: string;
}) {
	return (
		<Span className={STEP_LABEL_EXTRA_CLASS[status]} color="defaultLight" variant="body-xs">
			{children}
		</Span>
	);
}
